export type BusinessProposalPaymentType = "monthly" | "yearly";

export type BusinessProposalStatus =
  | "pending"
  | "accepted"
  | "paid"
  | "expired";

export interface BusinessProposalData {
  id: string;
  subscriptionId?: number;
  packageName: string;
  amount: number;
  paymentType: BusinessProposalPaymentType;
  currencySymbol?: string;
  currencyCode?: string;
  businessName?: string;
  contactEmail?: string;
  contactPhone?: string;
  planId?: number;
  customerEmail?: string;
  customerName?: string;
  description?: string;
  status?: BusinessProposalStatus;
  expiresAt?: string;
  isDemo?: boolean;
}

type NestedRecord = Record<string, unknown>;

function asRecord(value: unknown): NestedRecord | null {
  return value && typeof value === "object" ? (value as NestedRecord) : null;
}

function normalizePaymentType(value: unknown): BusinessProposalPaymentType {
  const raw = String(value || "monthly").toLowerCase();
  if (raw === "yearly" || raw === "annual" || raw === "year") return "yearly";
  return "monthly";
}

function normalizeStatus(value: unknown): BusinessProposalStatus {
  const raw = String(value || "pending").toLowerCase();
  if (raw === "paid" || raw === "active" || raw === "completed") return "paid";
  if (
    raw === "expired" ||
    raw === "cancelled" ||
    raw === "canceled" ||
    raw === "past_due"
  ) {
    return "expired";
  }
  if (raw === "accepted") return "accepted";
  // pending_setup, pending, inactive, etc.
  return "pending";
}

export function isDemoOnboardingId(encodedSubscriptionId: string): boolean {
  return encodedSubscriptionId.trim().toLowerCase() === "demo";
}

export function getOnboardingBillingCountry(_currencyCode?: string): string {
  // Glossa onboarding customers are UK-based (Stripe uses ISO code GB).
  return "GB";
}

export function buildOnboardingBillingDetails(proposal: BusinessProposalData) {
  return {
    email: proposal.customerEmail || undefined,
    name: proposal.customerName || proposal.businessName || undefined,
    address: {
      country: getOnboardingBillingCountry(proposal.currencyCode),
    },
  };
}

function resolveAmount(raw: NestedRecord, paymentType: BusinessProposalPaymentType): number {
  if (raw.amount_cents != null && Number.isFinite(Number(raw.amount_cents))) {
    return Number(raw.amount_cents) / 100;
  }

  if (raw.amount != null && Number.isFinite(Number(raw.amount))) {
    return Number(raw.amount);
  }

  if (raw.price != null && Number.isFinite(Number(raw.price))) {
    return Number(raw.price);
  }

  const pkg = asRecord(raw.package);
  if (pkg) {
    const packagePrice =
      paymentType === "yearly"
        ? pkg.annual_price ?? pkg.yearly_price
        : pkg.monthly_price;
    if (packagePrice != null && Number.isFinite(Number(packagePrice))) {
      return Number(packagePrice);
    }
  }

  return 0;
}

function resolveCurrencySymbol(raw: NestedRecord, company: NestedRecord | null): string {
  const companyCurrency = asRecord(company?.currency);
  const symbol =
    companyCurrency?.currency_symbol ??
    raw.currency_symbol ??
    raw.currencySymbol;

  if (typeof symbol === "string" && symbol.trim()) return symbol;

  const code = String(
    companyCurrency?.currency_code ?? raw.currency ?? "GBP",
  ).toUpperCase();

  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  if (code === "EUR") return "€";
  return "£";
}

/** Map GET /business-onboarding/{encoded} payload into UI model. */
export function normalizeBusinessProposalFromApi(
  raw: NestedRecord,
  encodedId: string,
): BusinessProposalData {
  const pkg = asRecord(raw.package);
  const company = asRecord(raw.company);
  const user = asRecord(raw.user);

  const paymentType = normalizePaymentType(
    raw.interval ?? raw.payment_type ?? raw.paymentType ?? raw.billing_cycle,
  );

  const amount = resolveAmount(raw, paymentType);
  const packageName = String(
    pkg?.name ??
      raw.name ??
      raw.package_name ??
      raw.packageName ??
      raw.plan_name ??
      "Glossa Package",
  );

  return {
    id: encodedId,
    subscriptionId:
      raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : undefined,
    packageName,
    amount,
    paymentType,
    currencySymbol: resolveCurrencySymbol(raw, company),
    currencyCode: String(
      asRecord(company?.currency)?.currency_code ?? raw.currency ?? "GBP",
    ).toUpperCase(),
    businessName: (company?.company_name ??
      raw.business_name ??
      raw.businessName) as string | undefined,
    // Glossa support contacts stay in footer branding by default.
    contactEmail: "support@getglossa.com",
    planId:
      raw.package_id != null
        ? Number(raw.package_id)
        : pkg?.id != null
          ? Number(pkg.id)
          : raw.plan_id != null
            ? Number(raw.plan_id)
            : undefined,
    customerEmail: (user?.email ??
      company?.company_email ??
      raw.customer_email) as string | undefined,
    customerName: (user?.name ?? raw.customer_name) as string | undefined,
    status: normalizeStatus(raw.status),
    expiresAt: (raw.ends_at ?? raw.expires_at ?? raw.expiresAt) as string | undefined,
    isDemo: false,
  };
}

export function buildBusinessProposalUrl(
  encodedSubscriptionId: string | number,
  baseUrl = "https://app.getglossa.com",
): string {
  return `${baseUrl}/business-onboarding/${encodedSubscriptionId}`;
}

/** Decode base64 subscription id from URL; fall back to raw value. */
export function decodeBusinessOnboardingId(encodedSubscriptionId: string): string {
  try {
    return atob(encodedSubscriptionId);
  } catch {
    return encodedSubscriptionId;
  }
}

export function formatProposalAmount(
  amount: number,
  currencySymbol = "£",
  paymentType: BusinessProposalPaymentType,
): string {
  const formatted = `${currencySymbol}${Number(amount || 0).toFixed(2)}`;
  return paymentType === "yearly" ? `${formatted} / year` : `${formatted} / month`;
}

export function getPaymentTypeLabel(paymentType: BusinessProposalPaymentType): string {
  return paymentType === "yearly" ? "Yearly" : "Monthly";
}

/** Demo proposal shown when API is unavailable (preview / local dev). */
export function getDemoBusinessProposal(id: string): BusinessProposalData {
  const isYearly = id.toLowerCase().includes("year");

  return {
    id,
    packageName: "Glossa Professional",
    amount: isYearly ? 990 : 99,
    paymentType: isYearly ? "yearly" : "monthly",
    currencySymbol: "£",
    currencyCode: "GBP",
    businessName: "Demo Salon & Spa",
    customerEmail: "owner@demosalon.com",
    customerName: "Sarah Johnson",
    contactEmail: "support@getglossa.com",
    planId: 2,
    description:
      "Thank you for your interest in Glossa. This is a demo business onboarding showing how your subscription package, billing details, and secure payment flow will appear to your customer.",
    status: "pending",
    isDemo: true,
  };
}
